import { useEffect, useRef, useState } from 'react';

/**
 * CameraCapture — opens a live webcam feed and lets the user snap a photo.
 * On mobile the OS camera sheet handles everything via a hidden <input capture>.
 * On desktop a modal with getUserMedia is shown.
 *
 * Props:
 *   onCapture(file: File) — called with the captured image as a File
 *   onClose()             — called when user dismisses without capturing
 *   label                 — button label shown on the snap button (default "Take Photo")
 */

const isMobile = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 600;

export default function CameraCapture({ onCapture, onClose, label = 'Take Photo' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const mobileInputRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const mobile = isMobile();

  useEffect(() => {
    if (mobile) {
      // Immediately trigger the native camera input on mobile
      mobileInputRef.current?.click();
      return;
    }

    // Desktop: start getUserMedia stream
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setReady(true);
        }
      })
      .catch(() => setError('Camera access denied or unavailable.'));

    return () => {
      // Stop stream on unmount
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [mobile]);

  const handleSnap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onCapture(file);
    }, 'image/jpeg', 0.92);
  };

  const handleMobileCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) onCapture(file);
    else onClose();
  };

  // Mobile: invisible input + immediate click — no visible UI needed
  if (mobile) {
    return (
      <input
        ref={mobileInputRef}
        type="file"
        accept="image/*"
        capture="user"
        hidden
        onChange={handleMobileCapture}
        onCancel={onClose}
      />
    );
  }

  // Desktop modal
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, overflow: 'hidden',
        width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #eee' }}>
          <span style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.95rem' }}>
            {label}
          </span>
          <button
            onClick={() => { streamRef.current?.getTracks().forEach(t => t.stop()); onClose(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 4 }}
            aria-label="Close camera"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Camera feed */}
        <div style={{ background: '#000', position: 'relative', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {error ? (
            <p style={{ color: '#ccc', fontSize: '0.85rem', textAlign: 'center', padding: 20 }}>{error}</p>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
            />
          )}
        </div>
        <canvas ref={canvasRef} hidden />

        {/* Controls */}
        <div style={{ padding: '16px 18px', display: 'flex', gap: 10 }}>
          <button
            className="btn-purple"
            onClick={handleSnap}
            disabled={!ready || !!error}
            style={{ flex: 1 }}
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="4" fill="currentColor" />
                <path d="M20.94 13A8 8 0 112 12a8 8 0 0118.94 1z" />
              </svg>
              Snap
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
